import React from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'July 1, 2025';

const Terms: React.FC = () => {
  return (
    <main className="bg-slate-50 dark:bg-slate-900">
      <div className="mx-auto max-w-4xl px-4 py-8 space-y-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100 sm:text-3xl">
            Terms &amp; Conditions
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Effective date: {EFFECTIVE_DATE}</p>
        </header>

        <article className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-sm space-y-6 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
          {/* §1 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">1. Acceptance of Terms</h2>
            <p>
              By accessing or using the TELLURVISION platform (&quot;Service&quot;), including the
              website, mobile applications, and billboard network, you agree to be bound by these
              Terms &amp; Conditions (&quot;Terms&quot;). If you do not agree, do not use the
              Service.
            </p>
          </section>

          {/* §2 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">2. Description of Service</h2>
            <p>
              TELLURVISION operates a digital billboard advertising platform that enables
              advertisers to create, schedule, and display advertising campaigns on physical
              billboard hardware and through social/digital channels. The platform includes campaign
              management tools, audience analytics, AI-assisted creative generation, payment
              processing, and zoning compliance features.
            </p>
          </section>

          {/* §3 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">3. Accounts &amp; Eligibility</h2>
            <p>
              You must be at least 18 years old and capable of entering a binding contract to create
              an account. You are responsible for maintaining the confidentiality of your credentials
              and for all activity under your account. You agree to notify us immediately of any
              unauthorized use.
            </p>
          </section>

          {/* §4 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">4. Advertising Content</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                All submitted creative content must comply with applicable federal, state, and local
                laws, including the Outdoor Advertising Association of America (OAAA) Code of
                Industry Principles.
              </li>
              <li>
                TELLURVISION reserves the right to reject or remove any content at its sole
                discretion, including content that is misleading, defamatory, obscene, or violates
                third-party intellectual property rights.
              </li>
              <li>
                You represent and warrant that you own or have obtained all necessary rights,
                licenses, and permissions for any content you upload.
              </li>
            </ul>
          </section>

          {/* §5 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">5. Payments &amp; Billing</h2>
            <p>
              Campaign purchases are processed through Stripe. When you submit a campaign,
              a payment hold (authorization) is placed on your payment method. Funds are captured
              only after administrative approval of your campaign.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>All prices are listed in US dollars unless otherwise indicated.</li>
              <li>
                Refunds for approved and aired campaigns are not available. Campaigns that are
                rejected before airing will have their payment hold released automatically.
              </li>
              <li>
                You are responsible for any taxes applicable to your transactions with
                TELLURVISION.
              </li>
            </ul>
          </section>

          {/* §6 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">6. Zoning &amp; Compliance</h2>
            <p>
              TELLURVISION provides zoning-law information and AI-assisted zoning guidance as a
              convenience. This information does not constitute legal advice. You are solely
              responsible for ensuring that your advertising content and placement comply with all
              applicable local zoning ordinances, sign codes, and regulations.
            </p>
          </section>

          {/* §7 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              7. AI-Generated Content Disclaimer
            </h2>
            <p>
              The Service may offer AI-powered features for creative generation, including text copy,
              campaign videos, and strategic recommendations. AI-generated content is provided
              &quot;as is&quot; without warranties of accuracy, fitness for a particular purpose, or
              non-infringement. You are responsible for reviewing and approving all AI-generated
              content before use.
            </p>
          </section>

          {/* §8 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              8. Intellectual Property
            </h2>
            <p>
              The TELLURVISION name, logo, software, and all related trademarks are the property
              of TELLURVISION or its licensors. Except for user-submitted advertising content, all
              platform content is protected by copyright and may not be reproduced without written
              permission.
            </p>
          </section>

          {/* §9 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              9. Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by law, TELLURVISION and its affiliates shall not be
              liable for any indirect, incidental, special, consequential, or punitive damages,
              including loss of profits, data, or business opportunities, arising out of or in
              connection with the Service. Our total aggregate liability shall not exceed the amounts
              you paid to TELLURVISION in the twelve (12) months preceding the claim.
            </p>
          </section>

          {/* §10 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              10. Dispute Resolution &amp; Arbitration
            </h2>
            <p>
              Any dispute or claim arising out of or relating to these Terms or the Service shall be
              resolved by binding arbitration administered by the American Arbitration Association
              (AAA) under its Commercial Arbitration Rules. Arbitration shall take place in the state
              of the advertiser&apos;s primary business address or, if that is unavailable, in
              Harris County, Texas.
            </p>
            <p>
              <strong>Class-action waiver:</strong> You agree that all claims will be brought in your
              individual capacity and not as a plaintiff or class member in any purported class or
              representative proceeding.
            </p>
          </section>

          {/* §11 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">11. Termination</h2>
            <p>
              We may suspend or terminate your account at any time for violation of these Terms or
              for any reason with 30 days&apos; written notice. Upon termination, your right to use
              the Service ceases immediately. Any campaign funds already captured are non-refundable;
              uncaptured holds will be released.
            </p>
          </section>

          {/* §12 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">12. Modifications</h2>
            <p>
              TELLURVISION may update these Terms at any time. We will notify registered users via
              email or in-app notification at least 14 days before material changes take effect.
              Continued use of the Service after changes constitutes acceptance of the revised Terms.
            </p>
          </section>

          {/* §13 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">13. Governing Law</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of the State of
              Texas, without regard to its conflict-of-law provisions.
            </p>
          </section>

          {/* §14 */}
          <section className="space-y-2">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">14. Contact</h2>
            <p>
              Questions about these Terms? Contact us at{' '}
              <a
                href="mailto:legal@TELLURVISION.com"
                className="text-blue-600 underline hover:text-blue-800"
              >
                legal@TELLURVISION.com
              </a>
              .
            </p>
          </section>
        </article>

        <div className="flex gap-3 text-sm">
          <Link
            to="/privacy"
            className="text-blue-600 underline hover:text-blue-800"
          >
            Privacy Policy
          </Link>
          <Link
            to="/"
            className="text-slate-500 dark:text-slate-400 underline hover:text-slate-700"
          >
            Back to home
          </Link>
        </div>
      </div>
    </main>
  );
};

export default Terms;
